/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import {
  ActionRequest,
  ActionSuccess,
  Folder,
  ActionFail,
  XError,
  SearchUtils,
  FileFormatToFileTypeMap,
  Constants,
  AssetsApi,
  AssetMediaType,
  ParentalControlSettings,
  HideUnsubscribed,
  Ducks,
} from '@wit/vtv-x-foundation-lib';
import { SagaIterator } from 'redux-saga';
import { all, put, select, takeLeading } from 'redux-saga/effects';
import { StoreState } from 'store/store';
import AppConfig from '../../../../../services/AppConfig';

export enum ActionType {
  REQUEST = 'entitlements/assets/subscriptionsFolders/REQUEST',
  SUCCESS = 'entitlements/assets/subscriptionsFolders/SUCCESS',
  FAIL = 'entitlements/assets/subscriptionsFolders/FAIL',
}

interface Meta {
  subscriptionsIds: string[];
}

type Request = ActionRequest<ActionType.REQUEST, Meta>;
type Success = ActionSuccess<ActionType.SUCCESS, Folder[]>;
type Fail = ActionFail<ActionType.FAIL>;

export type Actions = Request | Success | Fail;

export interface ReducerState {
  data: Folder[];
  processing?: boolean;
  processed?: boolean;
  error?: XError;
}

const Actions = {
  request: (subscriptionsIds: string[]): Request => ({
    type: ActionType.REQUEST,
    meta: {
      subscriptionsIds,
    },
  }),
  success: (folders: Folder[]): Success => ({
    type: ActionType.SUCCESS,
    payload: folders,
  }),
  fail: (error: XError): Fail => ({
    type: ActionType.FAIL,
    error,
  }),
};

export const ActionCreators = {
  getSubscriptionsFolders: Actions.request,
};

const initialState: ReducerState = {
  data: [],
};

export default function myHome(state = initialState, action: Actions): ReducerState {
  switch (action.type) {
    case ActionType.REQUEST:
      return {
        ...state,
        processing: true,
        processed: false,
        error: undefined, // reset error
      };
    case ActionType.SUCCESS:
      return {
        data: action.payload,
        processing: false,
        processed: true,
      };
    case ActionType.FAIL:
      return {
        data: initialState.data,
        processing: false,
        processed: true,
        error: action.error,
      };
    default:
      return state;
  }
}

// SELECTORS
export const Selectors = {
  isProcessing: (state: StoreState): boolean | undefined => state.entitlements.assets.subscriptionsFolders.processing,
  isProcessed: (state: StoreState): boolean | undefined => state.entitlements.assets.subscriptionsFolders.processed,
  getFolders: (state: StoreState): Folder[] => state.entitlements.assets.subscriptionsFolders.data,
};

export const PAGINATED_ENTITLEMENTS_NAMESPACE = 'ENTITLEMENTS_ASSETS_SUBSCRIPTIONS';

function* getSubscriptionsFoldersSaga(action: Request): SagaIterator {
  try {
    const { subscriptionsIds } = action.meta;

    if (subscriptionsIds.length > 0) {
      const ksql = SearchUtils.createFoldersFilter(subscriptionsIds, 'subscription_id');
      const filesFormat: FileFormatToFileTypeMap = yield select(Ducks.Dms.Selectors.getFilesFormat);
      const parentalControlSettings: ParentalControlSettings = yield select(Ducks.Dms.Selectors.getParentalControl);

      const result: Folder[] = yield Ducks.Pagination.callCompletedPaginatedApi(
        // TODO: Every time the entitlements are refresh, the namespace for the ppv and collections should be invalidated
        PAGINATED_ENTITLEMENTS_NAMESPACE,
        Constants.DEFAULT_API_PAGE_SIZE,
        AssetsApi.assetSearch,
        ksql,
        filesFormat,
        parentalControlSettings,
        [AssetMediaType.FOLDER],
        undefined,
        HideUnsubscribed[AppConfig.env.CHANNELS_UNSUBSCRIBED_VISIBILITY]
      );

      yield put(Actions.success(result));
    }
  } catch (error) {
    yield put(Actions.fail(XError.fromError(error)));
  }
}

export function* saga(): SagaIterator {
  yield all([takeLeading(ActionType.REQUEST, getSubscriptionsFoldersSaga)]);
}
